import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Layout from "../components/Layout";
import API from "../api/axios";
import { fetchWithFallback } from "../utils/fetchWithFallback";
import { useTheme } from "../context/ThemeContext";

export default function Categories() {
  const { theme } = useTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  // Shared theme styles
  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "20px",
    boxShadow: theme.cardShadow,
  };
  const input = {
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const fetchCategories = async () => {
    setLoading(true);
    const result = await fetchWithFallback("/categories/", "ss_cache_categories");
    if (result.data) setCategories(result.data);
    setLoading(false);
  };

  useEffect(() => { fetchCategories(); }, []);

  useEffect(() => {
    const handleSync = () => { fetchCategories(); };
    window.addEventListener("martsense-sync-complete", handleSync);
    return () => window.removeEventListener("martsense-sync-complete", handleSync);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post("/categories/", form);
      toast.success("Category created");
      fetchCategories();
    } catch {
      const { addToQueue } = await import("../utils/offlineQueue");
      await addToQueue({ method: "POST", url: "/categories/", data: form });
      const tempCat = { ...form, id: `offline_${Date.now()}`, product_count: 0 };
      setCategories(prev => [tempCat, ...prev]);
      toast.success("No internet — category saved offline and will sync automatically", { icon: "📵" });
    }
    setForm({ name: "", description: "" });
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category? Products in it will become uncategorized.")) return;
    try {
      await API.delete(`/categories/${id}`);
      toast.success("Category deleted");
      fetchCategories();
    } catch {
      toast.error("Failed to delete category");
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "32px", flexWrap: "wrap", gap: "16px",
      }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>
            Categories
          </h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
            {categories.length} categories
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
            color: "#0a0f0f", border: "none", borderRadius: "10px",
            padding: "12px 24px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
          }}
        >
          {showForm ? "Cancel" : "+ Add Category"}
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <div style={{ ...card, padding: "32px", marginBottom: "32px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "600", color: theme.textPrimary, marginBottom: "24px" }}>
            New Category
          </h2>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>
                Category Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Electronics, Food, Clothing"
                required
                style={input}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>
                Description (optional)
              </label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description"
                style={input}
              />
            </div>
            <button type="submit" style={{
              background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
              color: "#0a0f0f", border: "none", borderRadius: "10px",
              padding: "12px 32px", fontSize: "14px", fontWeight: "700",
              alignSelf: "flex-start", cursor: "pointer",
            }}>
              Save Category
            </button>
          </form>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : categories.length === 0 ? (
        <div style={{ textAlign: "center", color: theme.textSecondary, padding: "60px 0" }}>
          <p style={{ fontSize: "48px", marginBottom: "16px" }}>🗂️</p>
          <p>No categories yet. Create your first one above.</p>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: "16px",
        }}>
          {categories.map((cat) => (
            <div key={cat.id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div>
                  <p style={{ fontSize: "16px", fontWeight: "600", color: theme.textPrimary, marginBottom: "4px" }}>
                    {cat.name}
                  </p>
                  {cat.description && (
                    <p style={{ fontSize: "13px", color: theme.textSecondary }}>{cat.description}</p>
                  )}
                </div>
                <button onClick={() => handleDelete(cat.id)} style={{
                  background: "none", border: "none", color: "#e74c3c",
                  fontSize: "16px", cursor: "pointer", flexShrink: 0,
                }}>✕</button>
              </div>
              <span style={{
                background: theme.inputBg, color: "#2ec4b6",
                borderRadius: "20px", padding: "4px 12px",
                fontSize: "12px", fontWeight: "600",
              }}>
                {cat.product_count} products
              </span>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}